import React, { useState } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import { PencilIcon } from '@heroicons/react/outline'
import ConfirmationDialog from '../../leevo_ui/ConfirmationDialog/ConfirmationDialog'

function DisclosuresTable({ disclosures, onDisclosuresChange, onEditDisclosure, facilityId }) {
  const [updatingIds, setUpdatingIds] = useState(new Set())
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    disclosure: null,
    field: null,
    value: null
  })

  async function handleToggle(disclosure, field, newValue) {
    if (field === 'active' && disclosure.active && !newValue && disclosure.responseCount > 0) {
      setConfirmDialog({
        isOpen: true,
        disclosure,
        field,
        value: newValue
      })
      return
    }

    await performUpdate(disclosure, field, newValue)
  }

  async function performUpdate(disclosure, field, newValue) {
    const disclosureId = disclosure.id
    setUpdatingIds((prev) => new Set([...prev, disclosureId]))

    const optimisticDisclosures = disclosures.map((d) => (d.id === disclosureId ? { ...d, [field]: newValue } : d))
    onDisclosuresChange(optimisticDisclosures)

    try {
      await axios.put(`/api/facilities/${facilityId}/disclosures/${disclosureId}`, {
        disclosure: { [field]: newValue }
      })
    } catch (error) {
      const rollbackDisclosures = disclosures.map((d) => (d.id === disclosureId ? { ...d, [field]: !newValue } : d))
      onDisclosuresChange(rollbackDisclosures)

      const errorMessage =
        (error.response && error.response.data && error.response.data.error) || 'Failed to update disclosure'
      alert(`Error: ${errorMessage}`)
    } finally {
      setUpdatingIds((prev) => {
        const newSet = new Set(prev)
        newSet.delete(disclosureId)
        return newSet
      })
    }
  }

  async function handleConfirmToggle() {
    const { disclosure, field, value } = confirmDialog
    setConfirmDialog({
      isOpen: false,
      disclosure: null,
      field: null,
      value: null
    })
    await performUpdate(disclosure, field, value)
  }

  function getTypeLabel(type) {
    return type === 'one_liner' ? 'One-liner' : 'Document'
  }

  return (
    <>
      <div className="hidden overflow-x-auto lg:block">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
              >
                Name
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
              >
                Type
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
              >
                Active
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
              >
                Required
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
              >
                Responses
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {disclosures.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                  No disclosures created yet. Click &quot;Add New&quot; to create one.
                </td>
              </tr>
            ) : (
              disclosures.map((disclosure) => {
                const isUpdating = updatingIds.has(disclosure.id)

                return (
                  <tr key={disclosure.id} className={isUpdating ? 'opacity-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{disclosure.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{getTypeLabel(disclosure.disclosureType)}</div>
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={disclosure.active}
                          onChange={(e) => handleToggle(disclosure, 'active', e.target.checked)}
                          disabled={isUpdating}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                      </label>
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={disclosure.required}
                          onChange={(e) => handleToggle(disclosure, 'required', e.target.checked)}
                          disabled={isUpdating || disclosure.responseCount > 0}
                        />
                        <div
                          className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600 ${
                            disclosure.responseCount > 0 ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        ></div>
                      </label>
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap">
                      <span className="inline-flex px-2 text-xs font-semibold leading-5 text-purple-800 bg-purple-100 rounded-full">
                        {disclosure.responseCount || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap">
                      <button
                        onClick={() => onEditDisclosure(disclosure)}
                        disabled={isUpdating}
                        className="inline-flex items-center text-purple-600 hover:text-purple-900 disabled:opacity-50"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="space-y-4 lg:hidden">
        {disclosures.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500 bg-white rounded-lg">
            No disclosures created yet. Click &quot;Add New&quot; to create one.
          </div>
        ) : (
          disclosures.map((disclosure) => {
            const isUpdating = updatingIds.has(disclosure.id)

            return (
              <div key={disclosure.id} className={`bg-white rounded-lg shadow p-4 ${isUpdating ? 'opacity-50' : ''}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-base font-medium text-gray-900">{disclosure.name}</h3>
                    <p className="text-sm text-gray-500">{getTypeLabel(disclosure.disclosureType)}</p>
                  </div>
                  <button
                    onClick={() => onEditDisclosure(disclosure)}
                    disabled={isUpdating}
                    className="flex items-center justify-center flex-shrink-0 w-12 h-12 text-purple-600 rounded-lg hover:bg-purple-50 disabled:opacity-50"
                    aria-label="Edit disclosure"
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">Active</label>
                    <div className="p-2 -m-2">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={disclosure.active}
                          onChange={(e) => handleToggle(disclosure, 'active', e.target.checked)}
                          disabled={isUpdating}
                        />
                        <div className="w-14 h-8 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-purple-600"></div>
                      </label>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">{disclosure.active ? 'ON' : 'OFF'}</p>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">Required</label>
                    <div className="p-2 -m-2">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={disclosure.required}
                          onChange={(e) => handleToggle(disclosure, 'required', e.target.checked)}
                          disabled={isUpdating || disclosure.responseCount > 0}
                        />
                        <div
                          className={`w-14 h-8 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-purple-600 ${
                            disclosure.responseCount > 0 ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        ></div>
                      </label>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">{disclosure.required ? 'ON' : 'OFF'}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                  <span className="text-sm text-gray-600">Responses</span>
                  <span className="inline-flex px-3 py-1 text-sm font-semibold text-purple-800 bg-purple-100 rounded-full">
                    {disclosure.responseCount || 0}
                  </span>
                </div>
              </div>
            )
          })
        )}
      </div>

      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        onClose={() =>
          setConfirmDialog({
            isOpen: false,
            disclosure: null,
            field: null,
            value: null
          })
        }
        onConfirm={handleConfirmToggle}
        title="Deactivate Disclosure?"
        message={
          confirmDialog.disclosure ? (
            <span>
              This disclosure has <strong>{confirmDialog.disclosure.responseCount} response(s)</strong>. Deactivating it
              will prevent new registrants from seeing it, but existing responses will be preserved. Are you sure you
              want to continue?
            </span>
          ) : (
            ''
          )
        }
        confirmText="Deactivate"
        cancelText="Cancel"
        confirmColor="red"
      />
    </>
  )
}

DisclosuresTable.propTypes = {
  disclosures: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      disclosureType: PropTypes.string.isRequired,
      active: PropTypes.bool.isRequired,
      required: PropTypes.bool.isRequired,
      responseCount: PropTypes.number
    })
  ).isRequired,
  onDisclosuresChange: PropTypes.func.isRequired,
  onEditDisclosure: PropTypes.func.isRequired,
  facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
}

export default DisclosuresTable
